import { useState } from "react";

/**
 * "This card has changes you have not saved yet" — the state every Assets editor needed and none of them
 * carried (ART-50, Taylor: *"since i have to hit save, should the save button fill or change state in some
 * way so that it indicates a change has been made and needs to save?"*).
 *
 * Every editor on this page holds a DRAFT in local state, seeded from the server read and reset from the SAVE
 * RESPONSE rather than by remounting (the COMPONENTS.md draft contract). So "unsaved" is exactly *the draft
 * differs from the last shape the server confirmed*, and the baseline has to MOVE on each successful save —
 * comparing against the `initial` prop instead would report a freshly-saved card as dirty forever, which is
 * the same lie as never reporting it at all.
 *
 * The comparison is a JSON stamp. That is sound HERE and is not a general-purpose equality: both sides are
 * built by the same mapper (`toDraft`) from the same shape, so key order is stable, and every draft on this
 * page is plain JSON — no Dates, no Sets, no undefined-vs-absent distinction that carries meaning. Reach for
 * something stronger before using this over a shape that does not hold those properties.
 *
 * It drives the button's LOOK **and its `disabled`** (PO, 2026-09-07): a Save with nothing to save is not a
 * quiet button, it is an inert one, and offering a press that does nothing is the dead control this codebase
 * refuses elsewhere.
 *
 * That REVERSES what this file argued a day earlier, and the argument is worth keeping because it names the
 * failure to watch for: if a stamp ever misses a change, a look-only fill costs a button that seems calm
 * while there is something to save, whereas disabling turns the same miss into a card that CANNOT be saved —
 * silently, until it loses someone's work. What makes the trade acceptable is that a miss needs a specific
 * cause, and both are closed: a non-JSON value in a draft (there are none — every draft here is plain JSON
 * built by one mapper, and this stamp must not be reused over a shape carrying Dates, Sets, or a meaningful
 * undefined-vs-absent), or an edited field outside the stamp, which only `useUnsavedSections` can have and
 * which `epkSectionsCoverTheDraft` pins. Add a draft field with UI and no section and that test fails before
 * a user ever meets an unsavable card.
 */
export function useUnsavedChanges<T>(draft: T): {
  /** The draft has moved off what the server last confirmed. */
  unsaved: boolean;
  /** Call from the mutation's `onSuccess`, with the value the draft is being reset TO. */
  markSaved: (saved: T) => void;
} {
  const [baseline, setBaseline] = useState(() => JSON.stringify(draft));
  return {
    unsaved: JSON.stringify(draft) !== baseline,
    markSaved: (saved: T) => setBaseline(JSON.stringify(saved)),
  };
}

/**
 * The Save button's props for an editor's unsaved state — one place, so six cards cannot drift into six
 * different ideas of what "you have changes" looks like.
 *
 * `primary` is this system's INK/inverse fill (BRAND-3 keeps crimson off primary buttons), so a dirty card's
 * Save goes from a quiet ghost to a solid dark button. `data-unsaved` is what the tests assert — a class
 * name is a styling detail and would pin the test to the variant rather than to the state.
 *
 * **`blocked` is why this owns `disabled` rather than leaving it to the call site.** A caller writing its own
 * `disabled` after the spread silently overrides ours, so the two conditions have to meet in one expression:
 * `blocked` is the card's own reason it cannot save right now (in flight, over a length cap, an invalid
 * year), and "clean" is this hook's. Pass the first in; never re-declare `disabled` beside the spread.
 *
 * The `title` is deliberate too. A disabled control with no stated reason is the dead button this codebase
 * refuses, and "nothing has changed" is obvious to whoever just typed and invisible to everyone else — a
 * blocked card keeps its own explanation instead, since a caller's reason outranks ours.
 */
export const saveButtonState = (unsaved: boolean, blocked = false) =>
  ({
    variant: unsaved ? ("primary" as const) : ("ghost" as const),
    "data-unsaved": unsaved || undefined,
    disabled: blocked || !unsaved,
    title: !blocked && !unsaved ? "No changes to save" : undefined,
  });

/**
 * The per-SECTION form of the same question, for an editor whose ONE document is presented as several cards
 * with a Save button each. The EPK is the only one: Artist info · Media · Press quotes · Point Of Contact ·
 * Notable moments are five `Card`s over a single whole-document PUT.
 *
 * All five buttons saving the whole document is correct — it IS one document. Lighting all five when one
 * field moved is not: the fill means *this box has something to save*, so a shared stamp downgrades it to
 * *somewhere on this page something changed*, which is a sentence nobody can act on (ART-50, reported against
 * the shipped state: "a change in one of the boxes triggered all the save buttons not just the one in that
 * box"). Note the shared stamp was the ONLY reading available before ART-50, because there was one button
 * repeated five times and no dirty look at all — giving it a look is what made the sharing visible.
 *
 * `sections` maps a key to the draft fields that section owns, and it must be a module-level constant: the
 * baseline is stamped per key at mount, so a key appearing later has no baseline of its own and reads dirty
 * forever. Stamping a field LIST rather than an object also keeps order out of it.
 *
 * A field in NO section can never light anything. That is right for one with no UI (the EPK's `statPins`
 * round-trip untouched through every save) and a silent bug for one with UI, so the partition is pinned by a
 * test rather than trusted to stay current — see `EPK_SECTIONS`.
 */
export function useUnsavedSections<T extends object, K extends string>(
  draft: T,
  sections: Readonly<Record<K, readonly (keyof T)[]>>,
): { unsaved: Record<K, boolean>; markSaved: (saved: T) => void } {
  const stamp = (d: T) => {
    const out = {} as Record<K, string>;
    for (const key of Object.keys(sections) as K[]) out[key] = JSON.stringify(sections[key].map((f) => d[f]));
    return out;
  };
  const [baseline, setBaseline] = useState(() => stamp(draft));
  const now = stamp(draft);
  const unsaved = {} as Record<K, boolean>;
  for (const key of Object.keys(sections) as K[]) unsaved[key] = now[key] !== baseline[key];
  return { unsaved, markSaved: (saved: T) => setBaseline(stamp(saved)) };
}
